import type { ChangeEvent } from 'react'
import { useRef, useEffect } from 'react'

import styled from 'styled-components'

const FIELD_HEIGHT = '9rem'

const TextArea = styled.textarea`
  resize: none;
  overflow-y: hidden;
  min-height: ${FIELD_HEIGHT};
  border-radius: var(--input-border-radius);
  border: var(--input-border-width) solid var(--input-border-color);

  &:focus {
    outline: none !important;
  }

  &:focus-visible {
    border: var(--input-border-width) solid var(--blue-light) !important;
  }

  &::placeholder {
    color: var(--gray-700);
  }
`

const InputCounter = styled.div`
  bottom: 1rem;
  right: 0;
  color: var(--gray-500);
  background-color: var(--dropdown-background-color);

  span {
    color: inherit;
    font-size: inherit;
  }
`

export type CustomTextAreaProps = {
  value: string
  name: string
  dataTestId?: string
  placeholder: string
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void
  label?: string
  className?: string
  disabled?: boolean
  maxSize?: number
  currentSize?: number
}

const CustomTextArea = ({
  value,
  name,
  placeholder,
  onChange,
  label,
  className,
  dataTestId = '',
  disabled = false,
  maxSize = 0,
  currentSize = 0
}: CustomTextAreaProps) => {
  const commentInputRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const resizeTextArea = () => {
      const currentTarget = commentInputRef.current

      if (currentTarget && value.length) {
        currentTarget.style.height = FIELD_HEIGHT
        currentTarget.style.height = `${currentTarget.scrollHeight}px`
      }
    }

    resizeTextArea()
  }, [value])

  return (
    <div className={className}>
      {label && (
        <label htmlFor={name} className='hot-form__label'>
          {label}
        </label>
      )}
      <div className='_position-relative'>
        {maxSize > 0 ? (
          <InputCounter
            className='_position-absolute _d-flex _flex-row _align-items-center _justify-content-start _mr-3 _text-1'
            data-test='textarea-value-counter'>
            <span>{currentSize}</span>
            <span>/</span>
            <span>{maxSize}</span>
          </InputCounter>
        ) : null}
        <TextArea
          id={name}
          name={name}
          data-test={dataTestId}
          {...(maxSize && { maxLength: maxSize })}
          ref={commentInputRef}
          placeholder={placeholder}
          onChange={onChange}
          className='_w-full _p-3 _pt-3 _bg-transparent _text-gray-700'
          value={value}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

export default CustomTextArea
